import { useEffect, useRef, useState } from "react";
import SurveySheet from "./SurveySheet";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
} from "react-icons/fa";
import TitleStripe from "../common/TitleStripe";
import SurveyPoint from "./SurveyPoint";
import ReactApexChart from "react-apexcharts";
import { SURVEYS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { useQuery } from "react-query";
import { BsArrowReturnRight } from "react-icons/bs";
import { FiAlertOctagon, FiCameraOff } from "react-icons/fi";
import html2pdf from "html2pdf.js";
import { Alert, Button, Table } from "flowbite-react";
import moment from "moment";
import { IoAlertCircle } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import SurveyQuery from "./SurveyQuery";

const SurveyReview = ({ setActiveStep, activeStep, survey, setPdfSurvey }) => {
  const [listPintura, setListPintura] = useState([]);
  const [listEstrutura, setListEstrutura] = useState([]);
  const [error, setError] = useState(null);
  const [isGeneneratingPDF, setIsGeneratingPDF] = useState(false);
  const pdfRef = useRef();

  const generatePDF = async () => {
    setError(null);
    setIsGeneratingPDF(true);
    const style = document.createElement("style");
    document.head.appendChild(style);
    style.sheet?.insertRule(
      "body img:not(.logo-carro) { display: inline-block; }",
      style.sheet.cssRules.length
    );
    const element = pdfRef.current;
    const originalTransform = element.style.transform;
    element.style.backgroundColor = "white";

    element.style.transform = "scale(1)";
    element.style.transformOrigin = "top left";

    const opt = {
      margin: [0, 0, 0, 0], // Remove margens extras
      filename: "document.pdf",
      html2canvas: {
        scale: 1.1, // Aumenta a qualidade para evitar cortes
        useCORS: true, // Evita problemas de CORS para imagens
        allowTaint: true,
      },
      jsPDF: {
        unit: "px",
        format: [854, 1140],
        orientation: "portrait",
      },
    };

    try {
      const pdfBlob = await html2pdf().set(opt).from(element).outputPdf("blob");
      const pdfFile = new File([pdfBlob], "document.pdf", {
        type: "application/pdf",
      });
      const formData = new FormData();
      formData.append("file", pdfFile);
      formData.append("surveyId", surveyReceived?.id);
      formData.append(
        "plate",
        surveyReceived?.SurveyVehicle?.plate?.toUpperCase()
      );

      const pdfFileUrl = await axios.put(SURVEYS.FINISH, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setPdfSurvey(pdfFileUrl.data.data);
      setActiveStep(6);
    } catch (error) {
      setError(
        error?.response?.data?.message ||
          "Ops! Aconteceu algum erro, por favor tentar novamente em alguns minutos."
      );
    } finally {
      element.style.transform = originalTransform;
      style.remove();
      setIsGeneratingPDF(false);
    }
  };

  const {
    isLoading: isLoadingSurvey,
    error: errorSurvey,
    data: surveyReceived,
  } = useQuery(["surveyReceived"], async () => {
    const { data } = await axios.get(`${SURVEYS.GET_SURVEY}/${survey.id}`);

    setListPintura(
      data?.data?.responses?.filter(
        (item) => item?.question?.questionTypeId == 1
      )
    );

    setListEstrutura(
      data?.data?.responses?.filter(
        (item) => item?.question?.questionTypeId == 2
      )
    );

    return data.data;
  });

  const chunkArray = (array, size) => {
    const result = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  };

  const groupedImages = chunkArray(surveyReceived?.images || [], 16);

  const getColorType = (type) => {
    switch (+type) {
      case 1:
        return "green";
      case 2:
        return "yellow";
      case 3:
        return "red";
      default:
        return "gray";
    }
  };

  const getPaintColor = (name) => {
    const newName = name?.toUpperCase();

    if (newName?.includes("ORIGINAL")) {
      return "bg-green-600";
    }

    if (newName?.includes("QUEIMADA")) {
      return "bg-green-400";
    }

    if (newName?.includes("RISCADO")) {
      return "bg-yellow-500";
    }

    if (newName === "REPINTURA") {
      return "bg-yellow-300";
    }

    if (newName?.includes("AMASSADO")) {
      return "bg-orange-400";
    }

    if (newName?.includes("COM MASSA")) {
      return "bg-red-300";
    }

    if (newName?.includes("QUEBRADO")) {
      return "bg-red-500";
    }

    return "bg-gray-300";
  };

  const getStatusSurvey = () => {
    let question = surveyReceived?.responses?.find(
      (response) => response.questionId === 86
    );

    if (question?.id === 600) {
      return (
        <>
          <h2 className="text-green-500 font-semibold text-xl">Aprovado</h2>
          <FaCheckCircle color="green" fontSize={40} />
        </>
      );
    }
    if (question?.id === 601) {
      return (
        <>
          <h2 className="text-yellow-500 font-semibold text-xl">
            Aprovado com restrição
          </h2>
          <IoAlertCircle color="orange" fontSize={40} />
        </>
      );
    }
    if (question?.id === 602) {
      return (
        <>
          <h2 className="text-yellow-500 font-semibold text-xl">
            Aprovado com apontamento
          </h2>
          <IoAlertCircle color="orange" fontSize={40} />
        </>
      );
    }
    if (question?.id === 603) {
      return (
        <>
          <h2 className="text-red-500 font-semibold text-xl">Reprovado</h2>
          <IoAlertCircle color="red" fontSize={40} />
        </>
      );
    }

    return (
      <>
        <h2 className="text-gray-500 font-semibold text-xl">Não avaliado</h2>
        <IoAlertCircle color="gray" fontSize={40} />
      </>
    );
  };

  const image1 = surveyReceived?.images?.find((img) =>
    ["11", "42", "43"].includes(img?.id?.toString())
  );

  const image2 = surveyReceived?.images?.find((img) =>
    ["10", "75", "76"].includes(img?.id?.toString())
  );

  return (
    <div className="relative">
      {!isLoadingSurvey ? (
        <>
          {isGeneneratingPDF && (
            <div
              className="absolute top-0 bottom-0 right-0 left-0 bg-white z-10 opacity-100 flex flex-col gap-2 items-center justify-center h-full"
              style={{ zIndex: "10000" }}
            >
              <span className="loader-syncx"></span>
              <div>Emitindo laudo....</div>
            </div>
          )}
          <div
            className="overflow-auto overflow-y-scroll relative h-screen mt-5 mx-auto"
            style={{ maxHeight: "600px" }}
          >
            <div
              className="bg-gray-100 flex flex-col gap-4 pdf-sheet"
              ref={pdfRef}
            >
              <SurveySheet survey={surveyReceived}>
                <div
                  style={{ height: "800px" }}
                  className="flex items-center justify-center flex flex-col gap-2"
                >
                  <img
                    className="w-56 mb-2"
                    src={
                      surveyReceived?.configuration?.imgURL ||
                      "/assets/logo-verde.png"
                    }
                  />
                  <h1 className="text-4xl font-semibold uppercase mt-10">
                    Vistoria Cautelar
                  </h1>
                  <div className="flex items-center gap-2 mt-4">
                    {getStatusSurvey()}
                  </div>
                  {surveyReceived?.SurveyVehicle?.plate && (
                    <div className="text-3xl font-semibold mt-5 rounded-md border-2 border-black overflow-hidden">
                      <div
                        className="text-xs text-white text-center"
                        style={{ background: "#0933A4" }}
                      >
                        Brasil
                      </div>
                      <div className="px-2">
                        {surveyReceived?.SurveyVehicle?.plate?.toUpperCase()}
                      </div>
                    </div>
                  )}

                  {image1 && image2 && (
                    <div className="grid grid-cols-2 max-w-lg gap-4 justify-center mt-14">
                      <div className="w-60 h-60 bg-gray-200 rounded-md overflow-hidden relative shadow-md flex justify-center items-center">
                        {image1?.SurveyPhoto?.imgURL ? (
                          <img
                            src={image1?.SurveyPhoto?.imgURL}
                            className="object-contain mx-auto w-full"
                          />
                        ) : (
                          <div
                            className="absolute flex flex-col gap-2 justify-center items-center"
                            style={{
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%,-50%)",
                            }}
                          >
                            <FiCameraOff fontSize={30} />
                            <div className="text-center">
                              Imagem Indisponível
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="w-60 h-60 bg-gray-200 rounded-md overflow-hidden relative shadow-md flex justify-center items-center">
                        {image2?.SurveyPhoto?.imgURL ? (
                          <img
                            src={image2?.SurveyPhoto?.imgURL}
                            className="object-contain mx-auto w-full"
                          />
                        ) : (
                          <div
                            className="absolute flex flex-col gap-2 justify-center items-center"
                            style={{
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%,-50%)",
                            }}
                          >
                            <FiCameraOff fontSize={30} />
                            <div className="text-center">
                              Imagem Indisponível
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </SurveySheet>
              <SurveySheet survey={surveyReceived}>
                <div className="px-5 mt-5">
                  <TitleStripe>Dados do veículo</TitleStripe>
                  <div className="grid grid-cols-4 mt-5 gap-3 text-sm">
                    {surveyReceived?.SurveyVehicle?.img && (
                      <div className="border border-gray-300 shadow-md rounded-md h-40 w-full flex justify-center items-center gap-2">
                        <img
                          src={surveyReceived?.SurveyVehicle?.img}
                          className="object-contain h-32 rounded-md p-1"
                        />
                      </div>
                    )}

                    <div className="flex flex-col gap-2">
                      <div>
                        <div>Placa:</div>
                        <div className="font-semibold">
                          {surveyReceived?.SurveyVehicle?.plate?.toUpperCase() ||
                            "Não Informado"}
                        </div>
                      </div>
                      <div>
                        <div>Cor:</div>
                        <div className="font-semibold">
                          {surveyReceived?.SurveyVehicle?.color ||
                            "Não informado"}
                        </div>
                      </div>
                      <div>
                        <div>Ano Fabr./Modelo:</div>
                        <div className="font-semibold">
                          {surveyReceived?.SurveyVehicle?.yearMake
                            ? `${surveyReceived?.SurveyVehicle?.yearMake}/
                          ${surveyReceived?.SurveyVehicle?.yearModel}`
                            : "Não informado"}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div>
                        <div>Marca/Modelo:</div>
                        <div className="font-semibold">
                          {surveyReceived?.SurveyVehicle?.model ||
                            "Não informado"}
                        </div>
                      </div>
                      <div>
                        <div>Chassi:</div>
                        <div className="font-semibold">
                          {surveyReceived?.SurveyVehicle?.chassi?.toUpperCase() ||
                            "Não Informado"}
                        </div>
                      </div>
                      <div>
                        <div>Fabricante:</div>
                        <div className="font-semibold">
                          {surveyReceived?.SurveyVehicle?.maker ||
                            "Não informado"}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div>
                        <div>Renavam:</div>
                        <div className="font-semibold">
                          {surveyReceived?.SurveyVehicle?.renavam ||
                            "Não informado"}
                        </div>
                      </div>
                      <div>
                        <div>Motor:</div>
                        <div className="font-semibold">
                          {surveyReceived?.SurveyVehicle?.engine?.toUpperCase() ||
                            "Não informado"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-10 px-5">
                  <TitleStripe>Dados da vistoria</TitleStripe>
                  <div className="grid grid-cols-2 gap-3 mt-5">
                    <div>
                      <div>Proprietário:</div>
                      <div className="font-semibold capitalize">
                        {surveyReceived?.ownerName || "Não Informado"}
                      </div>
                    </div>
                    <div>
                      <div>Digitador:</div>
                      <div className="font-semibold">
                        {surveyReceived?.typist?.name}
                      </div>
                    </div>
                    <div>
                      <div>Vistoriador:</div>
                      <div className="font-semibold">
                        {surveyReceived?.surveyor?.name}
                      </div>
                    </div>
                    <div>
                      <div>Data da vistoria:</div>
                      <div className="font-semibold">
                        {moment(surveyReceived?.createdAt).format("DD/MM/yyyy")}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-10 px-5">
                  <TitleStripe>Itens verificados</TitleStripe>
                  <div
                    className={
                      listPintura?.length > 0 && listEstrutura?.length > 0
                        ? "grid gap-2 grid-cols-2 mt-2"
                        : "flex justify-center mt-2"
                    }
                  >
                    {listPintura?.length === 0 &&
                      listEstrutura?.length === 0 && (
                        <div className="text-center text-gray-500 italic border border-gray-300 p-2 mt-10 text-lg">
                          Nenhum item de Pintura ou Estrutura foi verificado.
                        </div>
                      )}
                    {/* Gráfico de Pintura */}
                    {listPintura?.length > 0 && (
                      <div>
                        <div className="text-center mt-3 underline font-semibold">
                          Pintura
                        </div>
                        <div className="w-full max-w-md">
                          <ReactApexChart
                            options={{
                              chart: {
                                type: "bar",
                                height: 350,
                                toolbar: {
                                  show: false,
                                },
                              },
                              plotOptions: {
                                bar: {
                                  horizontal: false,
                                  columnWidth: "45%",
                                  distributed: true,
                                },
                              },
                              dataLabels: {
                                enabled: true,
                                formatter: (val) => val,
                              },
                              xaxis: {
                                categories: [
                                  "Aprovados",
                                  "Com restrições",
                                  "Reprovados",
                                ],
                                labels: {
                                  style: {
                                    colors: ["#008001", "#FDBA48", "#FF0000"],
                                    fontSize: "14px",
                                  },
                                },
                              },
                              colors: ["#008001", "#FDBA48", "#FF0000"],
                            }}
                            series={[
                              {
                                name: "Pintura",
                                data: [
                                  listPintura.filter((x) => x.typeId === 1)
                                    .length,
                                  listPintura.filter((x) => x.typeId === 2)
                                    .length,
                                  listPintura.filter((x) => x.typeId === 3)
                                    .length,
                                ],
                              },
                            ]}
                            type="bar"
                            height={350}
                          />
                        </div>
                      </div>
                    )}

                    {/* Gráfico de Estrutura */}
                    {listEstrutura?.length > 0 && (
                      <div>
                        <div className="text-center mt-3 underline font-semibold">
                          Estrutura
                        </div>
                        <div className="w-full max-w-md">
                          <ReactApexChart
                            options={{
                              chart: {
                                type: "bar",
                                height: 350,
                                toolbar: {
                                  show: false,
                                },
                              },
                              plotOptions: {
                                bar: {
                                  horizontal: false,
                                  columnWidth: "45%",
                                  distributed: true,
                                },
                              },
                              dataLabels: {
                                enabled: true,
                                formatter: (val) => val,
                              },
                              xaxis: {
                                categories: [
                                  "Aprovados",
                                  "Com restrições",
                                  "Reprovados",
                                ],
                                labels: {
                                  style: {
                                    colors: ["#008001", "#FDBA48", "#FF0000"],
                                    fontSize: "14px",
                                  },
                                },
                              },
                              colors: ["#008001", "#FDBA48", "#FF0000"],
                            }}
                            series={[
                              {
                                name: "Estrutura",
                                data: [
                                  listEstrutura.filter((x) => x.typeId === 1)
                                    .length,
                                  listEstrutura.filter((x) => x.typeId === 2)
                                    .length,
                                  listEstrutura.filter((x) => x.typeId === 3)
                                    .length,
                                ],
                              },
                            ]}
                            type="bar"
                            height={350}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </SurveySheet>
              {listPintura?.length > 0 && (
                <SurveySheet survey={surveyReceived}>
                  <div className="px-5 mt-5">
                    <TitleStripe>
                      Análise e originalidade da pintura
                    </TitleStripe>
                  </div>
                  <div className="grid grid-cols-3 px-5 gap-20 mt-10">
                    <div className="flex flex-col gap-20 col-span-2 pl-10">
                      <div className="relative">
                        <img src="/assets/survey/car-left-view.png" />
                        {listPintura?.some((item) => item.questionId == 42) && (
                          <SurveyPoint
                            number={42}
                            style={{
                              top: "40px",
                              left: "120px",
                            }}
                            className={`${getPaintColor(
                              listPintura?.find((item) => item.questionId == 42)
                                ?.name
                            )}`}
                          />
                        )}

                        {listPintura?.some((item) => item.questionId == 43) && (
                          <SurveyPoint
                            number={43}
                            style={{
                              top: "40px",
                              left: "200px",
                            }}
                            className={`${getPaintColor(
                              listPintura?.find((item) => item.questionId == 43)
                                ?.name
                            )}`}
                          />
                        )}

                        {listPintura?.some((item) => item.questionId == 44) && (
                          <SurveyPoint
                            number={44}
                            style={{
                              top: "40px",
                              right: "120px",
                            }}
                            className={`${getPaintColor(
                              listPintura?.find((item) => item.questionId == 44)
                                ?.name
                            )}`}
                          />
                        )}

                        {listPintura?.some((item) => item.questionId == 53) && (
                          <SurveyPoint
                            number={53}
                            style={{
                              top: "10px",
                              left: "180px",
                            }}
                            className={`${getPaintColor(
                              listPintura?.find((item) => item.questionId == 53)
                                ?.name
                            )}`}
                          />
                        )}

                        {listPintura?.some((item) => item.questionId == 59) && (
                          <SurveyPoint
                            number={59}
                            style={{
                              top: "0px",
                              left: "260px",
                            }}
                            className={`${getPaintColor(
                              listPintura?.find((item) => item.questionId == 59)
                                ?.name
                            )}`}
                          />
                        )}

                        {listPintura?.some((item) => item.questionId == 54) && (
                          <SurveyPoint
                            number={54}
                            style={{
                              top: "5px",
                              right: "90px",
                            }}
                            className={`${getPaintColor(
                              listPintura?.find((item) => item.questionId == 54)
                                ?.name
                            )}`}
                          />
                        )}

                        {listPintura?.some((item) => item.questionId == 45) && (
                          <SurveyPoint
                            number={45}
                            style={{
                              top: "25px",
                              right: "55px",
                            }}
                            className={`${getPaintColor(
                              listPintura?.find((item) => item.questionId == 45)
                                ?.name
                            )}`}
                          />
                        )}
                      </div>
                      <div className="relative">
                        <img src="/assets/survey/car-right-view.png" />
                        {listPintura?.some((item) => item.questionId == 52) && (
                          <SurveyPoint
                            number={52}
                            style={{
                              top: "40px",
                              right: "120px",
                            }}
                            className={`${getPaintColor(
                              listPintura?.find((item) => item.questionId == 52)
                                ?.name
                            )}`}
                          />
                        )}
                        {listPintura?.some((item) => item.questionId == 51) && (
                          <SurveyPoint
                            number={51}
                            style={{
                              top: "40px",
                              right: "200px",
                            }}
                            className={`${getPaintColor(
                              listPintura?.find((item) => item.questionId == 51)
                                ?.name
                            )}`}
                          />
                        )}

                        {listPintura?.some((item) => item.questionId == 50) && (
                          <SurveyPoint
                            number={50}
                            style={{
                              top: "40px",
                              left: "120px",
                            }}
                            className={`${getPaintColor(
                              listPintura?.find((item) => item.questionId == 50)
                                ?.name
                            )}`}
                          />
                        )}

                        {listPintura?.some((item) => item.questionId == 56) && (
                          <SurveyPoint
                            number={56}
                            style={{
                              top: "10px",
                              right: "180px",
                            }}
                            className={`${getPaintColor(
                              listPintura?.find((item) => item.questionId == 56)
                                ?.name
                            )}`}
                          />
                        )}

                        {listPintura?.some((item) => item.questionId == 60) && (
                          <SurveyPoint
                            number={60}
                            style={{
                              top: "0px",
                              right: "260px",
                            }}
                            className={`${getPaintColor(
                              listPintura?.find((item) => item.questionId == 60)
                                ?.name
                            )}`}
                          />
                        )}

                        {listPintura?.some((item) => item.questionId == 57) && (
                          <SurveyPoint
                            number={57}
                            style={{
                              top: "5px",
                              left: "90px",
                            }}
                            className={`${getPaintColor(
                              listPintura?.find((item) => item.questionId == 57)
                                ?.name
                            )}`}
                          />
                        )}

                        {listPintura?.some((item) => item.questionId == 49) && (
                          <SurveyPoint
                            number={49}
                            style={{
                              top: "25px",
                              left: "55px",
                            }}
                            className={`${getPaintColor(
                              listPintura?.find((item) => item.questionId == 49)
                                ?.name
                            )}`}
                          />
                        )}

                        {listPintura?.some((item) => item.questionId == 41) && (
                          <SurveyPoint
                            number={41}
                            style={{
                              top: "70px",
                              right: "10px",
                            }}
                            className={`${getPaintColor(
                              listPintura?.find((item) => item.questionId == 41)
                                ?.name
                            )}`}
                          />
                        )}

                        {listPintura?.some((item) => item.questionId == 47) && (
                          <SurveyPoint
                            number={47}
                            style={{
                              top: "70px",
                              left: "10px",
                            }}
                            className={`${getPaintColor(
                              listPintura?.find((item) => item.questionId == 47)
                                ?.name
                            )}`}
                          />
                        )}
                      </div>
                    </div>
                    <div className="relative">
                      <img src="/assets/survey/car-top-view.png" />
                      {listPintura?.some((item) => item.questionId == 40) && (
                        <SurveyPoint
                          number={40}
                          style={{
                            top: "40px",
                            left: "60px",
                          }}
                          className={`${getPaintColor(
                            listPintura?.find((item) => item.questionId == 40)
                              ?.name
                          )}`}
                        />
                      )}

                      {listPintura?.some((item) => item.questionId == 48) && (
                        <SurveyPoint
                          number={48}
                          style={{
                            top: "180px",
                            left: "60px",
                          }}
                          className={`${getPaintColor(
                            listPintura?.find((item) => item.questionId == 48)
                              ?.name
                          )}`}
                        />
                      )}

                      {listPintura?.some((item) => item.questionId == 46) && (
                        <SurveyPoint
                          number={46}
                          style={{
                            bottom: "20px",
                            left: "60px",
                          }}
                          className={`${getPaintColor(
                            listPintura?.find((item) => item.questionId == 46)
                              ?.name
                          )}`}
                        />
                      )}
                    </div>
                  </div>

                  <ul className="bg-gray-200 rounded-md p-2 mx-8 mt-3 flex items-center text-2xs gap-2 justify-between">
                    <li className="flex items-center gap-1">
                      <div className="rounded-full w-3 h-3 bg-green-600"></div>
                      <div>Pintura original</div>
                    </li>
                    <li className="flex items-center gap-1">
                      <div className="rounded-full w-3 h-3 bg-green-400"></div>
                      <div>Pintura queimada</div>
                    </li>
                    <li className="flex items-center gap-1">
                      <div className="rounded-full w-3 h-3 bg-yellow-500"></div>
                      <div>Riscado</div>
                    </li>
                    <li className="flex items-center gap-1">
                      <div className="rounded-full w-3 h-3 bg-yellow-300"></div>
                      <div>Repintura</div>
                    </li>
                    <li className="flex items-center gap-1">
                      <div className="rounded-full w-3 h-3 bg-orange-400"></div>
                      <div>Amassado</div>
                    </li>
                    <li className="flex items-center gap-1">
                      <div className="rounded-full w-3 h-3 bg-red-300"></div>
                      <div>Repintura com massa</div>
                    </li>
                    <li className="flex items-center gap-1">
                      <div className="rounded-full w-3 h-3 bg-red-500"></div>
                      <div>Quebrado</div>
                    </li>
                  </ul>

                  <ul className="grid grid-cols-3 text-xs px-10 mt-4 gap-3">
                    {listPintura?.map((item, index) => (
                      <li className="flex items-start gap-2" key={index}>
                        <div
                          className={`rounded-full w-3 h-3 mt-1 ${getPaintColor(
                            item.name
                          )}`}
                          style={{ minWidth: "12px" }}
                        ></div>
                        <div className="text-2xs">
                          <div className="font-semibold">
                            {item.question?.id} - {item.question?.name}
                          </div>
                          <div className="flex items-center gap-2 -ml-4">
                            <BsArrowReturnRight />
                            <span>{item.name}</span>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </SurveySheet>
              )}

              {listEstrutura?.length > 0 && (
                <SurveySheet survey={surveyReceived}>
                  <div className="px-5 mt-10">
                    <TitleStripe>
                      Análise e Originalidade de Estrutura e Agregados
                    </TitleStripe>
                  </div>
                  <div className="grid grid-cols-2 px-5 gap-5 mt-5">
                    <div className="relative">
                      <img src="/assets/survey/car-chassi-right-view.png" />
                      {listEstrutura?.some((item) => item.questionId == 18) && (
                        <SurveyPoint
                          number={18}
                          style={{
                            top: "55px",
                            right: "45px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 18
                              )?.typeId
                            ),
                          }}
                        />
                      )}

                      {listEstrutura?.some((item) => item.questionId == 33) && (
                        <SurveyPoint
                          number={33}
                          style={{
                            top: "60px",
                            right: "75px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 33
                              )?.typeId
                            ),
                          }}
                        />
                      )}

                      {listEstrutura?.some((item) => item.questionId == 30) && (
                        <SurveyPoint
                          number={30}
                          style={{
                            top: "15px",
                            right: "125px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 30
                              )?.typeId
                            ),
                          }}
                        />
                      )}

                      {listEstrutura?.some((item) => item.questionId == 31) && (
                        <SurveyPoint
                          number={31}
                          style={{
                            top: "10px",
                            left: "105px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 31
                              )?.typeId
                            ),
                          }}
                        />
                      )}

                      {listEstrutura?.some((item) => item.questionId == 39) && (
                        <SurveyPoint
                          number={39}
                          style={{
                            top: "55px",
                            left: "45px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 39
                              )?.typeId
                            ),
                          }}
                        />
                      )}

                      {listEstrutura?.some((item) => item.questionId == 31) && (
                        <SurveyPoint
                          number={31}
                          style={{
                            top: "60px",
                            left: "75px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 31
                              )?.typeId
                            ),
                          }}
                        />
                      )}

                      {listEstrutura?.some((item) => item.questionId == 27) && (
                        <SurveyPoint
                          number={27}
                          style={{
                            top: "70px",
                            left: "165px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 27
                              )?.typeId
                            ),
                          }}
                        />
                      )}

                      {listEstrutura?.some((item) => item.questionId == 24) && (
                        <SurveyPoint
                          number={24}
                          style={{
                            top: "30px",
                            left: "35px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 24
                              )?.typeId
                            ),
                          }}
                        />
                      )}
                    </div>
                    <div className="relative mt-5">
                      <img src="/assets/survey/car-chassi-right-view-1.png" />
                      {listEstrutura?.some((item) => item.questionId == 15) && (
                        <SurveyPoint
                          number={15}
                          style={{
                            top: "20px",
                            right: "20px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 15
                              )?.typeId
                            ),
                          }}
                        />
                      )}
                      {listEstrutura?.some((item) => item.questionId == 26) && (
                        <SurveyPoint
                          number={26}
                          style={{
                            top: "20px",
                            left: "15px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 26
                              )?.typeId
                            ),
                          }}
                        />
                      )}
                    </div>
                    <div className="relative">
                      <img src="/assets/survey/car-chassi-left-view.png" />
                      {listEstrutura?.some((item) => item.questionId == 19) && (
                        <SurveyPoint
                          number={19}
                          style={{
                            top: "55px",
                            left: "45px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 19
                              )?.typeId
                            ),
                          }}
                        />
                      )}

                      {listEstrutura?.some((item) => item.questionId == 35) && (
                        <SurveyPoint
                          number={35}
                          style={{
                            top: "60px",
                            left: "75px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 35
                              )?.typeId
                            ),
                          }}
                        />
                      )}

                      {listEstrutura?.some((item) => item.questionId == 20) && (
                        <SurveyPoint
                          number={20}
                          style={{
                            top: "15px",
                            left: "125px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 20
                              )?.typeId
                            ),
                          }}
                        />
                      )}

                      {listEstrutura?.some((item) => item.questionId == 38) && (
                        <SurveyPoint
                          number={38}
                          style={{
                            top: "55px",
                            left: "110px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 38
                              )?.typeId
                            ),
                          }}
                        />
                      )}

                      {listEstrutura?.some((item) => item.questionId == 21) && (
                        <SurveyPoint
                          number={21}
                          style={{
                            top: "15px",
                            left: "183px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 21
                              )?.typeId
                            ),
                          }}
                        />
                      )}

                      {listEstrutura?.some((item) => item.questionId == 22) && (
                        <SurveyPoint
                          number={22}
                          style={{
                            top: "10px",
                            right: "105px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 22
                              )?.typeId
                            ),
                          }}
                        />
                      )}

                      {listEstrutura?.some((item) => item.questionId == 37) && (
                        <SurveyPoint
                          number={37}
                          style={{
                            top: "55px",
                            right: "45px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 37
                              )?.typeId
                            ),
                          }}
                        />
                      )}

                      {listEstrutura?.some((item) => item.questionId == 36) && (
                        <SurveyPoint
                          number={36}
                          style={{
                            top: "60px",
                            right: "75px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 36
                              )?.typeId
                            ),
                          }}
                        />
                      )}

                      {listEstrutura?.some((item) => item.questionId == 23) && (
                        <SurveyPoint
                          number={23}
                          style={{
                            top: "70px",
                            right: "165px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 23
                              )?.typeId
                            ),
                          }}
                        />
                      )}
                    </div>
                    <div className="relative mt-5">
                      <img src="/assets/survey/car-chassi-left-view-1.png" />
                      {listEstrutura?.some((item) => item.questionId == 16) && (
                        <SurveyPoint
                          number={16}
                          style={{
                            top: "20px",
                            left: "15px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 16
                              )?.typeId
                            ),
                          }}
                        />
                      )}
                      {listEstrutura?.some((item) => item.questionId == 25) && (
                        <SurveyPoint
                          number={25}
                          style={{
                            top: "20px",
                            right: "15px",
                            background: getColorType(
                              listEstrutura?.find(
                                (item) => item.questionId == 25
                              )?.typeId
                            ),
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <div>
                    <ul className="text-xs grid grid-cols-2 px-7 mt-10 gap-x-2 gap-y-2">
                      {listEstrutura?.map((item, index) => (
                        <li className="flex items-start gap-2" key={index}>
                          <div
                            className="rounded-full w-3 h-3 bg-green-500 mt-1"
                            style={{
                              minWidth: "12px",
                              background: getColorType(item.typeId),
                            }}
                          ></div>
                          <div className="text-xs">
                            <div className="font-semibold">
                              {item.question?.id} - {item.question?.name}
                            </div>
                            <div className="flex items-center gap-2 -ml-4">
                              <BsArrowReturnRight />
                              <span>{item.name}</span>
                            </div>
                            {item?.SurveyResponse?.comment && (
                              <div className="flex items-start gap-2 -ml-4">
                                <span>
                                  <span className="font-semibold">Obs:</span>{" "}
                                  {item?.SurveyResponse?.comment}
                                </span>
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </SurveySheet>
              )}

              <SurveySheet survey={surveyReceived}>
                <div className="px-5 mt-5">
                  <TitleStripe>Condição e numeração do chassi</TitleStripe>
                  <div className="grid grid-cols-2 gap-2 mt-5">
                    <div className="w-80 h-80 bg-gray-200 rounded-md overflow-hidden relative shadow-md flex justify-center items-center">
                      {surveyReceived?.images?.find((img) => img?.id == "1")
                        ?.SurveyPhoto?.imgURL ? (
                        <img
                          src={
                            surveyReceived?.images?.find(
                              (img) => img?.id == "1"
                            )?.SurveyPhoto?.imgURL || ""
                          }
                          className="object-contain w-full"
                        />
                      ) : (
                        <div
                          className="absolute flex flex-col gap-2 justify-center items-center"
                          style={{
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%,-50%)",
                          }}
                        >
                          <FiCameraOff fontSize={30} />
                          <div className="text-center">Imagem Indisponível</div>
                        </div>
                      )}
                    </div>
                    <ul>
                      <li className="flex items-start gap-2">
                        <div className="text-base flex gap-2 items-center mb-4">
                          <div className="font-semibold">Nº do chassi:</div>
                          <div>{surveyReceived?.SurveyVehicle?.chassi}</div>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="text-base flex gap-2 items-center mb-4">
                          <div className="font-semibold">
                            Padrão de fábrica:
                          </div>
                          <div>
                            {surveyReceived?.SurveyVehicle
                              ?.isChassiFactoryStandard
                              ? "Sim"
                              : "Não"}
                          </div>
                        </div>
                      </li>
                      {surveyReceived?.SurveyVehicle?.chassiObservation !==
                        "" && (
                        <li className="flex items-start gap-2">
                          <div className="text-base flex gap-2 items-center mb-4">
                            <div className="font-semibold">Observação:</div>
                            <div>
                              {surveyReceived?.SurveyVehicle?.chassiObservation}
                            </div>
                          </div>
                        </li>
                      )}
                      {surveyReceived?.responses
                        ?.filter((item) => item?.question?.questionTypeId == 3)
                        .map((item, index) => (
                          <li className="flex items-start gap-2" key={index}>
                            <div
                              className="rounded-full w-3 h-3 bg-green-500 mt-1"
                              style={{
                                minWidth: "12px",
                                background: getColorType(item.typeId),
                              }}
                            ></div>
                            <div className="text-base">
                              <div className="font-semibold">
                                {item.question?.name}
                              </div>
                              <div className="flex items-start gap-2 -ml-4">
                                <BsArrowReturnRight
                                  className="mt-1"
                                  style={{ minWidth: "20px" }}
                                />
                                <span>{item.name}</span>
                              </div>
                              {item?.SurveyResponse?.comment && (
                                <div className="flex items-start gap-2 mt-4 -ml-4">
                                  <span>
                                    <span className="font-semibold">Obs:</span>{" "}
                                    {item?.SurveyResponse?.comment}
                                  </span>
                                </div>
                              )}
                            </div>
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>

                <div className="px-5 mt-10 grouped-images">
                  <TitleStripe>Condição e numeração do motor</TitleStripe>
                  <div className="grid grid-cols-2 gap-2 mt-5">
                    <div className="w-80 h-80 bg-gray-200 rounded-md overflow-hidden relative shadow-md flex justify-center items-center">
                      {surveyReceived?.images?.find((img) => img?.id == "3")
                        ?.SurveyPhoto?.imgURL ? (
                        <img
                          src={
                            surveyReceived?.images?.find(
                              (img) => img?.id == "3"
                            )?.SurveyPhoto?.imgURL || ""
                          }
                          className="object-contain mx-auto w-full"
                        />
                      ) : (
                        <div
                          className="absolute flex flex-col gap-2 justify-center items-center"
                          style={{
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%,-50%)",
                          }}
                        >
                          <FiCameraOff fontSize={30} />
                          <div className="text-center">Imagem Indisponível</div>
                        </div>
                      )}
                    </div>
                    <ul>
                      <li className="flex items-start gap-2">
                        <div className="text-base flex gap-2 items-center mb-4">
                          <div className="font-semibold">Nº do motor:</div>
                          <div>{surveyReceived?.SurveyVehicle?.engine}</div>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="text-base flex gap-2 items-center mb-4">
                          <div className="font-semibold">
                            Padrão de fábrica:
                          </div>
                          <div>
                            {surveyReceived?.SurveyVehicle
                              ?.isEngineFactoryStandard
                              ? "Sim"
                              : "Não"}
                          </div>
                        </div>
                      </li>
                      {surveyReceived?.SurveyVehicle?.engineObservation !==
                        "" && (
                        <li className="flex items-start gap-2">
                          <div className="text-base flex gap-2 items-center mb-4">
                            <div className="font-semibold">Observação:</div>
                            <div>
                              {surveyReceived?.SurveyVehicle?.engineObservation}
                            </div>
                          </div>
                        </li>
                      )}

                      {surveyReceived?.responses
                        ?.filter((item) => item?.question?.questionTypeId == 4)
                        .map((item, index) => (
                          <li className="flex items-start gap-2" key={index}>
                            <div
                              className="rounded-full w-3 h-3 bg-green-500 mt-1"
                              style={{
                                minWidth: "12px",
                                background: getColorType(item.typeId),
                              }}
                            ></div>
                            <div className="text-base">
                              <div className="font-semibold">
                                {item.question?.name}
                              </div>
                              <div className="flex items-start gap-2 -ml-4">
                                <BsArrowReturnRight
                                  className="mt-1"
                                  style={{ minWidth: "20px" }}
                                />
                                <span>{item.name}</span>
                              </div>
                              {item?.SurveyResponse?.comment && (
                                <div className="flex items-start gap-2 mt-4 -ml-4">
                                  <span>
                                    <span className="font-semibold">Obs:</span>{" "}
                                    {item?.SurveyResponse?.comment}
                                  </span>
                                </div>
                              )}
                            </div>
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              </SurveySheet>

              {surveyReceived?.responses?.some((item) =>
                [5, 6, 7, 8, 14].includes(item?.question?.questionTypeId)
              ) && (
                <SurveySheet survey={surveyReceived}>
                  <div className="grid grid-cols-2 mt-1 px-5 gap-4">
                    {surveyReceived?.responses?.filter(
                      (item) => item?.question?.questionTypeId == 5
                    )?.length > 0 && (
                      <div>
                        <TitleStripe>
                          Condição da numeração do câmbio
                        </TitleStripe>
                        <ul className="mt-4">
                          {surveyReceived?.responses
                            ?.filter(
                              (item) => item?.question?.questionTypeId == 14
                            )
                            .map((item, index) => (
                              <li
                                className="flex items-start gap-2"
                                key={index}
                              >
                                <div
                                  className="rounded-full w-3 h-3 bg-green-500 mt-1"
                                  style={{ minWidth: "12px" }}
                                ></div>
                                <div className="text-sm">
                                  <div className="font-semibold">
                                    {item.question?.name}
                                  </div>
                                  <div className="flex items-center gap-2 -ml-4">
                                    <BsArrowReturnRight />
                                    <span>{item.name}</span>
                                  </div>
                                  {item?.SurveyResponse?.comment && (
                                    <div className="flex items-start gap-2 mt-1 -ml-4">
                                      <span>
                                        <span className="font-semibold">
                                          Obs:
                                        </span>{" "}
                                        {item?.SurveyResponse?.comment}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}

                    {surveyReceived?.responses?.filter(
                      (item) => item?.question?.questionTypeId == 5
                    )?.length > 0 && (
                      <div>
                        <TitleStripe>Odômetro</TitleStripe>
                        <ul className="mt-4">
                          {surveyReceived?.responses
                            ?.filter(
                              (item) => item?.question?.questionTypeId == 5
                            )
                            .map((item, index) => (
                              <li
                                className="flex items-start gap-2"
                                key={index}
                              >
                                <div
                                  className="rounded-full w-3 h-3 bg-green-500 mt-1"
                                  style={{ minWidth: "12px" }}
                                ></div>
                                <div className="text-sm">
                                  <div className="font-semibold">
                                    {item.question?.name}
                                  </div>
                                  <div className="flex items-center gap-2 -ml-4">
                                    <BsArrowReturnRight />
                                    <span>{item.name}</span>
                                  </div>
                                  {item?.SurveyResponse?.comment && (
                                    <div className="flex items-start gap-2 mt-1 -ml-4">
                                      <span>
                                        <span className="font-semibold">
                                          Obs:
                                        </span>{" "}
                                        {item?.SurveyResponse?.comment}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {surveyReceived?.responses?.filter(
                    (item) => item?.question?.questionTypeId == 6
                  )?.length > 0 && (
                    <div className="px-5 mt-5">
                      <TitleStripe>
                        (ETA) Condição das etiquetas de identificação do chassi
                      </TitleStripe>
                      <ul className="mt-4 grid grid-cols-2 gap-2">
                        {surveyReceived?.responses
                          ?.filter(
                            (item) => item?.question?.questionTypeId == 6
                          )
                          .map((item, index) => (
                            <li className="flex items-start gap-2" key={index}>
                              <div
                                className="rounded-full w-3 h-3 bg-green-500 mt-1"
                                style={{ minWidth: "12px" }}
                              ></div>
                              <div className="text-sm">
                                <div className="font-semibold">
                                  {item.question?.name}
                                </div>
                                <div className="flex items-center gap-2 -ml-4">
                                  <BsArrowReturnRight />
                                  <span>{item.name}</span>
                                </div>
                                {item?.SurveyResponse?.comment && (
                                  <div className="flex items-start gap-2 mt-1 -ml-4">
                                    <span>
                                      <span className="font-semibold">
                                        Obs:
                                      </span>{" "}
                                      {item?.SurveyResponse?.comment}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}

                  {surveyReceived?.responses?.filter(
                    (item) => item?.question?.questionTypeId == 7
                  )?.length > 0 && (
                    <div className="px-5 mt-5">
                      <TitleStripe>
                        (VIS) Condição da gravação do chassi nos vidros
                      </TitleStripe>
                      <ul className="mt-4 grid grid-cols-2 gap-2">
                        {surveyReceived?.responses
                          ?.filter(
                            (item) => item?.question?.questionTypeId == 7
                          )
                          .map((item, index) => (
                            <li className="flex items-start gap-2" key={index}>
                              <div
                                className="rounded-full w-3 h-3 bg-green-500 mt-1"
                                style={{ minWidth: "12px" }}
                              ></div>
                              <div className="text-sm">
                                <div className="font-semibold">
                                  {item.question?.name}
                                </div>
                                <div className="flex items-center gap-2 -ml-4">
                                  <BsArrowReturnRight />
                                  <span>{item.name}</span>
                                </div>
                                {item?.SurveyResponse?.comment && (
                                  <div className="flex items-start gap-2 mt-1 -ml-4">
                                    <span>
                                      <span className="font-semibold">
                                        Obs:
                                      </span>{" "}
                                      {item?.SurveyResponse?.comment}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}

                  {surveyReceived?.responses?.filter(
                    (item) => item?.question?.questionTypeId == 8
                  )?.length > 0 && (
                    <div className="px-5 mt-5">
                      <TitleStripe>
                        Condição estrutural da carroceria
                      </TitleStripe>
                      <ul className="mt-4 grid grid-cols-2 gap-2">
                        {surveyReceived?.responses
                          ?.filter(
                            (item) => item?.question?.questionTypeId == 8
                          )
                          .map((item, index) => (
                            <li className="flex items-start gap-2" key={index}>
                              <div
                                className="rounded-full w-3 h-3 bg-green-500 mt-1"
                                style={{ minWidth: "12px" }}
                              ></div>
                              <div className="text-sm">
                                <div className="font-semibold">
                                  {item.question?.name}
                                </div>
                                <div className="flex items-center gap-2 -ml-4">
                                  <BsArrowReturnRight />
                                  <span>{item.name}</span>
                                </div>
                                {item?.SurveyResponse?.comment && (
                                  <div className="flex items-start gap-2 mt-1 -ml-4">
                                    <span>
                                      <span className="font-semibold">
                                        Obs:
                                      </span>{" "}
                                      {item?.SurveyResponse?.comment}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                </SurveySheet>
              )}

              {groupedImages?.length > 0 &&
                groupedImages.map((imageGroup, groupIndex) => (
                  <SurveySheet key={groupIndex} survey={surveyReceived}>
                    <div className="px-5 mt-10">
                      <TitleStripe>Imagens {groupIndex + 1}</TitleStripe>
                      <div className="mt-5 grid grid-cols-4 gap-3 grouped-images">
                        {imageGroup.map((img, index) => (
                          <div>
                            <div
                              className="flex flex-col items-center h-40 bg-gray-200 relative overflow-hidden rounded-md shadow-md flex justify-center items-center border border-gray-100"
                              key={index}
                            >
                              <img
                                src={`${
                                  img?.SurveyPhoto?.imgURL
                                }?t=${Date.now()}-${index}`}
                                className="object-contain w-full"
                              />
                            </div>
                            <div className="text-center font-semibold text-2xs mt-1 capitalize underline">
                              {img?.name?.toLowerCase()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </SurveySheet>
                ))}

              <SurveySheet survey={surveyReceived}>
                <div className="px-5 mt-5">
                  <TitleStripe>
                    Considerações finais do avaliador/consultor/responsável
                    técnico
                  </TitleStripe>
                  <div className="mt-2">
                    {survey?.responses?.find(
                      (question) => question.questionId == 83
                    )?.SurveyResponse?.comment || "Não informado"}
                  </div>

                  <TitleStripe>Descritivo Veicular</TitleStripe>
                  <div className="mt-2">
                    {survey?.responses?.find(
                      (question) => question.questionId == 84
                    )?.SurveyResponse?.comment || "Não informado"}
                  </div>

                  <TitleStripe>Recomenda o veículo?</TitleStripe>
                  <div className="mt-2">
                    {survey?.responses?.find(
                      (question) => question.questionId == 85
                    )?.name || "Não informado"}
                  </div>
                  <TitleStripe>Conclusão final</TitleStripe>
                  {(() => {
                    const conclusion =
                      surveyReceived?.responses?.find(
                        (question) => question.questionId == 86
                      )?.name || "Não informado";

                    let styles = {
                      icon: null,
                      bg: "bg-gray-100",
                      border: "border-gray-400",
                      text: "text-gray-700",
                      label: "Não informado",
                    };

                    if (conclusion === "Aprovado") {
                      styles = {
                        icon: (
                          <FaCheckCircle className="text-green-600 text-2xl" />
                        ),
                        bg: "bg-green-50",
                        border: "border-green-400",
                        text: "text-green-800",
                        label: "Aprovado",
                      };
                    } else if (
                      conclusion === "Aprovado com restrição" ||
                      conclusion === "Aprovado com apontamento"
                    ) {
                      styles = {
                        icon: (
                          <FaExclamationTriangle className="text-yellow-600 text-2xl" />
                        ),
                        bg: "bg-yellow-50",
                        border: "border-yellow-400",
                        text: "text-yellow-800",
                        label: "Aprovado com restrições",
                      };
                    } else if (conclusion === "Reprovado") {
                      styles = {
                        icon: (
                          <FaTimesCircle className="text-red-600 text-2xl" />
                        ),
                        bg: "bg-red-50",
                        border: "border-red-400",
                        text: "text-red-800",
                        label: "Reprovado",
                      };
                    }

                    return (
                      <div
                        className={`mt-4 p-6 rounded-xl border-2 shadow-md flex flex-col items-center gap-3 ${styles.bg} ${styles.border}`}
                      >
                        <div className="flex items-center gap-2">
                          {styles.icon}
                          <span
                            className={`text-xl font-bold uppercase ${styles.text}`}
                          >
                            {styles.label}
                          </span>
                        </div>
                        <div className="text-center text-sm text-gray-600 italic">
                          Resultado da avaliação conforme os critérios técnicos
                          definidos.
                        </div>
                      </div>
                    );
                  })()}

                  <div
                    className="flex justify-center items-center gap-1 flex-col"
                    style={{ marginTop: "100px" }}
                  >
                    <div className="max-w-sm border-b border-gray-800 w-full"></div>
                    <div>{surveyReceived?.surveyor?.name}</div>
                    <div>{`[Vistoriador]`}</div>
                  </div>
                </div>
              </SurveySheet>

              <SurveyQuery surveyReceived={surveyReceived} />
            </div>
          </div>
          <div className="flex gap-2 items-center justify-center mt-4 ">
            <Button
              className="bg-gray-500 px-4"
              onClick={() => setActiveStep(4)}
            >
              Voltar
            </Button>
            <Button
              onClick={generatePDF}
              isProcessing={isGeneneratingPDF}
              disabled={isGeneneratingPDF}
              className="primary"
            >
              {!isGeneneratingPDF ? "Emitir laudo" : "Emitindo..."}
            </Button>
          </div>
          {error && (
            <Alert
              withBorderAccent
              color="red"
              className="mt-5 mx-auto"
              style={{ maxWidth: "800px" }}
            >
              {error}
            </Alert>
          )}
        </>
      ) : (
        <div className="relative" style={{ height: "700px" }}>
          <div
            className="absolute top-0 bottom-0 right-0 left-0 bg-white z-10 opacity-100 flex flex-col gap-2 items-center justify-center h-full"
            style={{ zIndex: "10000" }}
          >
            <span className="loader-syncx"></span>
            <div>Carregando laudo....</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyReview;
